<template>
    <div class="flex items-center w-full">
        <div
            v-if="prefix"
            class="bg-stone-100 px-3 py-2 shadow-xs border-l border-y border-stone-200 rounded-l-lg2 text-gray-600"
        >
            {{ prefix }}
        </div>
        <input
            :id="randomId"
            v-bind="$attrs"
            class="bg-white px-3 py-2 shadow-xs border border-stone-200 placeholder-gray-300 w-full disabled:bg-stone-100 disabled:text-gray-500 disabled:select-none disabled:cursor-not-allowed"
            :class="{
                'rounded-r-lg2': prefix,
                'rounded-lg2': !prefix,
                'focus:ring-primary focus:border-primary':
                    !useBookingFormThemeCssVariables,
                'focus:ring-booking-form-theme-main-background-color focus:border-booking-form-theme-main-background-color':
                    useBookingFormThemeCssVariables,
            }"
            @input="handleInputEvent($event)"
            :value="inputValue"
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, type PropType } from 'vue'
    import useId from '@/Shared/Hook/useId'
    import useElementFocus from '@/Shared/Hook/useElementFocus'

    defineOptions({
        inheritAttrs: false,
    })

    const emits = defineEmits<{
        'update:modelValue': [value: string | null]
    }>()

    type TextInputValue = string | number | boolean | null

    const props = defineProps({
        value: {
            type: null as unknown as PropType<TextInputValue | undefined>,
            default: undefined,
        },
        modelValue: {
            type: null as unknown as PropType<TextInputValue>,
            default: null,
        },
        shouldFocusOnMounted: {
            type: Boolean,
            default: false,
        },
        prefix: {
            type: String,
            default: null,
        },
        useBookingFormThemeCssVariables: {
            type: Boolean,
            default: false,
        },
    })

    const inputValue = computed(() => {
        if (props.value !== undefined) {
            return props.value
        }

        return props.modelValue
    })

    const handleInputEvent = (event: Event) => {
        const target = event.target as HTMLInputElement | null
        const value = target?.value ?? null
        emits('update:modelValue', value)
    }

    const randomId = useId().make()

    onMounted(() => {
        if (!props.shouldFocusOnMounted) {
            return
        }

        useElementFocus().focus(randomId)
    })
</script>
